import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Camera, Users } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { staffAPI } from '../../services/api';
import StaffTabs from './StaffTabs';
import { Badge, Card, EmptyState, AttentionStrip, Button, Spinner } from '../../components/ui';

const STATUS_VARIANT = {
  PRESENT: 'success',
  ABSENT: 'danger',
  HALF_DAY: 'warning',
};

export default function Attendance() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editing, setEditing] = useState({});

  const fetchList = async () => {
    setLoading(true);
    try {
      const [staffRes, attRes] = await Promise.all([
        staffAPI.list({ status: 'ACTIVE' }),
        staffAPI.getAttendanceByDate(date)
      ]);
      const staffList = staffRes.data || [];
      const attList = attRes.data || [];
      const merged = staffList.map((s) => {
        const a = attList.find((x) => x.staff_id === s.id);
        return a ? { ...a, staff_name: s.name, staff_role: s.role } : { staff_id: s.id, staff_name: s.name, staff_role: s.role, attendance_date: date, check_in_time: null, check_out_time: null, total_hours: null, status: 'PRESENT' };
      });
      setList(merged);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [date]);

  const updateAttendance = async (staffId, payload) => {
    setSavingId(staffId);
    try {
      await staffAPI.addAttendance({
        staff_id: staffId,
        attendance_date: date,
        ...payload,
      });
      fetchList();
      setEditing((e) => ({ ...e, [staffId]: null }));
    } catch (err) {
      alert(err.message || 'Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <StaffTabs active="attendance" />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <Calendar className="h-7 w-7 text-ga-primary" />
              Attendance
            </h1>
            <p className="text-ga-text-muted mt-1">Mark check-in / check-out by date</p>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="attendance-date" className="sr-only">Attendance date</label>
            <input
              id="attendance-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="ga-input px-4 py-2 rounded-ga-md"
            />
          </div>
        </div>

        <AttentionStrip
          tone="info"
          icon={Camera}
          title="Selfie check-in available"
          message="Staff can self-mark attendance with a geo-tagged selfie."
          action={(
            <Button as={Link} to="/dashboard/selfie-attendance" variant="secondary" className="px-3 py-1.5 text-xs">
              Open selfie attendance
            </Button>
          )}
        />

        {loading ? (
          <div className="py-12 flex justify-center">
            <Spinner />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            card
            icon={Users}
            title="No active staff"
            description="Add staff first from Staff Management to start marking attendance."
            action={<Button as={Link} to="/dashboard/staff">Go to Staff Management</Button>}
          />
        ) : (
          <Card padding="none" className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ga-border-subtle text-left text-ga-text-muted">
                    <th className="p-3 font-medium">Staff</th>
                    <th className="p-3 font-medium">Role</th>
                    <th className="p-3 font-medium">Check-in</th>
                    <th className="p-3 font-medium">Check-out</th>
                    <th className="p-3 font-medium">Hours</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => (
                    <tr key={`${row.staff_id}-${date}`} className="border-b border-ga-border-subtle/50">
                      <td className="p-3 font-medium text-ga-text-primary">{row.staff_name}</td>
                      <td className="p-3 text-ga-text-muted">{row.staff_role}</td>
                      <td className="p-3">
                        <input
                          type="time"
                          aria-label={`Check-in time for ${row.staff_name}`}
                          value={editing[row.staff_id]?.check_in_time ?? (row.check_in_time || '').slice(0, 5)}
                          onChange={(e) => setEditing((prev) => ({ ...prev, [row.staff_id]: { ...prev[row.staff_id], check_in_time: e.target.value } }))}
                          className="ga-input w-28 py-1 rounded-ga-sm"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="time"
                          aria-label={`Check-out time for ${row.staff_name}`}
                          value={editing[row.staff_id]?.check_out_time ?? (row.check_out_time || '').slice(0, 5)}
                          onChange={(e) => setEditing((prev) => ({ ...prev, [row.staff_id]: { ...prev[row.staff_id], check_out_time: e.target.value } }))}
                          className="ga-input w-28 py-1 rounded-ga-sm"
                        />
                      </td>
                      <td className="p-3 text-ga-text-primary">{row.total_hours != null ? Number(row.total_hours).toFixed(1) : '—'}</td>
                      <td className="p-3">
                        <Badge variant={STATUS_VARIANT[row.status] || 'neutral'}>{row.status}</Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <label htmlFor={`attendance-status-${row.staff_id}`} className="sr-only">{`Status for ${row.staff_name}`}</label>
                          <select
                            id={`attendance-status-${row.staff_id}`}
                            value={editing[row.staff_id]?.status ?? row.status}
                            onChange={(e) => setEditing((prev) => ({ ...prev, [row.staff_id]: { ...prev[row.staff_id], status: e.target.value } }))}
                            className="ga-input w-24 py-1 rounded-ga-sm text-xs"
                          >
                            <option value="PRESENT">Present</option>
                            <option value="HALF_DAY">Half day</option>
                            <option value="ABSENT">Absent</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => updateAttendance(row.staff_id, {
                              check_in_time: editing[row.staff_id]?.check_in_time || row.check_in_time,
                              check_out_time: editing[row.staff_id]?.check_out_time || row.check_out_time,
                              status: editing[row.staff_id]?.status || row.status,
                            })}
                            disabled={savingId === row.staff_id}
                            className="text-ga-primary text-xs font-medium hover:underline disabled:opacity-50"
                          >
                            {savingId === row.staff_id ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
